import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Save, QrCode, Camera, X, CheckCircle, AlertCircle } from 'lucide-react';
import jsQR from 'jsqr';
import api from '../../services/api';
import { useObras, useTensoes } from './useEstoqueConfig';

export default function EstoqueNovo() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tokenFromUrl = searchParams.get('qrtoken');

  const [form, setForm] = useState({
    status: 'disponivel',
    obra_atual: 'Escritório',
    intervalo_manutencao_dias: 180,
  });
  const [saving, setSaving] = useState(false);
  const [erro, setErro] = useState('');

  // QR Code pré-impresso
  const [qrToken, setQrToken] = useState(tokenFromUrl || '');
  const [qrInfo, setQrInfo] = useState(null);   // { numero_etiqueta } ou { vinculado: true }
  const [qrErro, setQrErro] = useState('');
  const [scanOpen, setScanOpen] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const intervalRef = useRef(null);

  const { data: cats } = useQuery({
    queryKey: ['estoque-cats'],
    queryFn: () => api.get('/estoque/categorias').then(r => r.data.data),
  });
  const obras = useObras();
  const tensoes = useTensoes();

  // Se veio token pela URL (do scanner), valida automaticamente
  useEffect(() => {
    if (tokenFromUrl) validarToken(tokenFromUrl);
  }, [tokenFromUrl]);

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  async function validarToken(token) {
    setQrErro(''); setQrInfo(null);
    if (!token) return;
    try {
      const { data } = await api.get(`/estoque/itens/qr/${token}`);
      if (data.status === 'vinculado') {
        setQrErro(`Este QR já está vinculado ao item "${data.data.nome}" (${data.data.codigo}).`);
        setQrToken('');
      } else if (data.status === 'pregerado') {
        setQrToken(token);
        setQrInfo({ numero_etiqueta: data.numero_etiqueta });
      }
    } catch {
      setQrErro('QR Code não reconhecido. Gere as etiquetas pelo módulo Gerador de QR antes de usar.');
      setQrToken('');
    }
  }

  // Câmera para escanear
  async function iniciarCamera() {
    setQrErro(''); setScanOpen(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
      videoRef.current.srcObject = stream;
      videoRef.current.play();
      intervalRef.current = setInterval(lerFrame, 300);
    } catch {
      setQrErro('Não foi possível acessar a câmera.');
      setScanOpen(false);
    }
  }

  function pararCamera() {
    clearInterval(intervalRef.current);
    if (videoRef.current?.srcObject) videoRef.current.srcObject.getTracks().forEach(t => t.stop());
    setScanOpen(false);
  }

  function lerFrame() {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || video.readyState !== video.HAVE_ENOUGH_DATA) return;
    const ctx = canvas.getContext('2d');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const code = jsQR(imageData.data, imageData.width, imageData.height);
    if (code?.data) {
      pararCamera();
      try {
        const url = new URL(code.data);
        const token = url.searchParams.get('token');
        if (token) validarToken(token);
        else setQrErro('QR Code inválido — não é uma etiqueta do sistema.');
      } catch {
        setQrErro('QR Code inválido.');
      }
    }
  }

  useEffect(() => () => pararCamera(), []);

  async function salvar() {
    if (!form.nome?.trim()) { setErro('Nome é obrigatório'); return; }
    setSaving(true); setErro('');
    try {
      const payload = { ...form };
      if (qrToken) payload.qrcode_token = qrToken;
      const { data } = await api.post('/estoque/itens', payload);
      navigate(`/estoque/itens/${data.data.id}`);
    } catch (e) {
      setErro(e.response?.data?.error || 'Erro ao salvar');
    } finally { setSaving(false); }
  }

  return (
    <div className="p-6 max-w-2xl mx-auto space-y-4">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/estoque/itens')} className="text-gray-400 hover:text-gray-600 p-1 rounded-lg">
          <ArrowLeft size={20} />
        </button>
        <h1 className="text-xl font-bold text-gray-800">Novo Item de Estoque</h1>
      </div>

      {/* QR Code pré-impresso */}
      <div className="bg-white rounded-xl shadow p-5 space-y-3">
        <div className="flex items-center gap-2 mb-1">
          <QrCode size={18} className="text-blue-600" />
          <h2 className="text-sm font-semibold text-gray-700">QR Code da etiqueta metálica</h2>
          <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">opcional</span>
        </div>
        <p className="text-xs text-gray-500">Se a ferramenta já tem uma etiqueta QR pré-impressa, escaneie aqui para vinculá-la. Caso contrário, o sistema gerará um código automático.</p>

        {/* Câmera de scan */}
        {scanOpen && (
          <div className="space-y-2">
            <div className="relative bg-black rounded-xl overflow-hidden" style={{ aspectRatio: '1', maxWidth: 280 }}>
              <video ref={videoRef} className="w-full h-full object-cover" playsInline muted />
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="w-40 h-40 border-2 border-white rounded-xl opacity-70" />
              </div>
            </div>
            <canvas ref={canvasRef} className="hidden" />
            <button onClick={pararCamera} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700">
              <X size={14} /> Cancelar scan
            </button>
          </div>
        )}

        {/* Estado do token */}
        {!scanOpen && (
          <>
            {qrToken && qrInfo && (
              <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-xl px-4 py-3">
                <CheckCircle size={18} className="text-green-600 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-green-800">Etiqueta vinculada: {qrInfo.numero_etiqueta}</p>
                  <p className="text-xs text-green-600 font-mono">{qrToken}</p>
                </div>
                <button onClick={() => { setQrToken(''); setQrInfo(null); setQrErro(''); }}
                  className="text-green-400 hover:text-green-600 p-1 rounded-lg">
                  <X size={16} />
                </button>
              </div>
            )}

            {!qrToken && (
              <button onClick={iniciarCamera}
                className="flex items-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 rounded-xl text-sm font-medium text-gray-600 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50 transition-colors w-full justify-center">
                <Camera size={18} /> Escanear QR Code pré-impresso
              </button>
            )}

            {qrErro && (
              <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-3 py-2 text-sm text-red-700">
                <AlertCircle size={16} className="flex-shrink-0" />
                <span>{qrErro}</span>
              </div>
            )}
          </>
        )}
      </div>

      {/* Formulário */}
      <div className="bg-white rounded-xl shadow p-6 space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Nome *</label>
            <input value={form.nome || ''} onChange={e => set('nome', e.target.value)} placeholder="Ex: Furadeira Martelete Bosch"
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Categoria</label>
            <select value={form.categoria_id || ''} onChange={e => set('categoria_id', e.target.value)}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400">
              <option value="">Selecione...</option>
              {(cats || []).map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Marca</label>
            <input value={form.marca || ''} onChange={e => set('marca', e.target.value)} placeholder="Bosch, Makita, Stanley..."
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Modelo</label>
            <input value={form.modelo || ''} onChange={e => set('modelo', e.target.value)}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tensão</label>
            <select value={form.tensao || ''} onChange={e => set('tensao', e.target.value)}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400">
              <option value="">—</option>
              {tensoes.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status inicial</label>
            <select value={form.status} onChange={e => set('status', e.target.value)}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400">
              <option value="disponivel">Disponível</option>
              <option value="em_uso">Em uso</option>
              <option value="manutencao">Em manutenção</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Obra / Local</label>
            <select value={form.obra_atual || ''} onChange={e => set('obra_atual', e.target.value)}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400">
              <option value="">—</option>
              {obras.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Responsável atual</label>
            <input value={form.responsavel_atual || ''} onChange={e => set('responsavel_atual', e.target.value)}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nº de Série</label>
            <input value={form.numero_serie || ''} onChange={e => set('numero_serie', e.target.value)}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Valor estimado (R$)</label>
            <input type="number" value={form.valor_estimado || ''} onChange={e => set('valor_estimado', e.target.value)}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Data de aquisição</label>
            <input type="date" value={form.data_aquisicao || ''} onChange={e => set('data_aquisicao', e.target.value)}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Intervalo manutenção (dias)</label>
            <input type="number" value={form.intervalo_manutencao_dias} onChange={e => set('intervalo_manutencao_dias', e.target.value)}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
          </div>
          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Observações</label>
            <textarea value={form.observacoes || ''} onChange={e => set('observacoes', e.target.value)} rows={2}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
          </div>
        </div>

        {erro && <p className="text-sm text-red-600 bg-red-50 rounded-xl px-3 py-2">{erro}</p>}

        <div className="flex gap-3 pt-2">
          <button onClick={() => navigate('/estoque/itens')} className="flex-1 px-4 py-2.5 border border-gray-200 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-50">
            Cancelar
          </button>
          <button onClick={salvar} disabled={saving}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
            <Save size={16} />
            {saving ? 'Salvando...' : qrToken ? 'Salvar e vincular QR' : 'Salvar'}
          </button>
        </div>
      </div>
    </div>
  );
}
